import { useEffect, useRef, useState } from 'preact/hooks'
import { FileText } from 'lucide-preact'
import { useLandingPageTemplatePreview } from '@/hooks/useLandingPageTemplatePreview'
import type { LandingPageTemplateListItemDto } from '@/types/landingPage'
import { templatePreviewHtml } from './templateText'

/** The page is laid out at a desktop width and scaled down, so the thumbnail shows the desktop page, not a squeezed one. */
const PAGE_WIDTH = 1280
const PAGE_HEIGHT = 800

const SIZES = {
    /** The builder's picker rows. */
    row: { width: 96, frame: 'w-24 h-[60px] rounded' },
    /** The Templates page's rows. */
    list: { width: 160, frame: 'w-40 h-[100px] rounded-md' },
} as const

interface TemplateThumbnailProps {
    workspaceId: string
    template: LandingPageTemplateListItemDto
    /** False when the template has no page to show (nothing signed off, or it can't be used), so nothing is read. */
    hasPage: boolean
    size: keyof typeof SIZES
}

/**
 * The top of a template's landing page, scaled down from its live preview. Decorative: the row it sits in already
 * names the template, so it is hidden from assistive technology, takes no focus and passes clicks through. Each one
 * reads a whole page, so it waits until it scrolls into view, as the console's templates view does.
 */
export function TemplateThumbnail({ workspaceId, template, hasPage, size }: TemplateThumbnailProps) {
    const frameRef = useRef<HTMLSpanElement>(null)
    const visible = useInView(frameRef, hasPage)
    const { preview, error } = useLandingPageTemplatePreview(workspaceId, hasPage && visible ? template.WebId : null)
    const { width, frame } = SIZES[size]
    const html = preview?.Html ? templatePreviewHtml(template, preview.Html) : null

    return (
        <span ref={frameRef} aria-hidden="true" className={`relative block shrink-0 overflow-hidden bg-white dark:bg-slate-700 pointer-events-none ${frame}`}>
            {html ? (
                <iframe
                    srcdoc={html}
                    sandbox="allow-scripts"
                    tabIndex={-1}
                    title=""
                    className="absolute left-0 top-0 border-0 origin-top-left"
                    style={{ width: `${PAGE_WIDTH}px`, height: `${PAGE_HEIGHT}px`, transform: `scale(${width / PAGE_WIDTH})` }}
                />
            ) : !hasPage || error || preview ? (
                <span className="absolute inset-0 flex items-center justify-center bg-slate-50 dark:bg-slate-800 text-slate-300 dark:text-slate-500">
                    <FileText className="w-5 h-5" />
                </span>
            ) : (
                <span className="absolute inset-0 bg-shimmer-light animate-shimmer" />
            )}
            <span className="absolute inset-0 rounded-[inherit] ring-1 ring-inset ring-slate-900/10 dark:ring-slate-50/20" />
        </span>
    )
}

/** True once the element has scrolled into view, and from then on. Without IntersectionObserver (jsdom) it is at once. */
function useInView(ref: { current: HTMLElement | null }, watching: boolean): boolean {
    const [inView, setInView] = useState(false)

    useEffect(() => {
        const node = ref.current
        if (!watching || inView || !node) return
        if (typeof IntersectionObserver === 'undefined') {
            setInView(true)
            return
        }
        const observer = new IntersectionObserver(entries => {
            if (!entries.some(entry => entry.isIntersecting)) return
            observer.disconnect()
            setInView(true)
        })
        observer.observe(node)
        return () => observer.disconnect()
    }, [ref, watching, inView])

    return inView
}
